/**
 * Every customer-facing string on the Agent Autonomy settings page, hoisted
 * so the copy pass is one file and the components carry no literals.
 *
 * All wording is TODO(copy) — final wording is Laura's. Rules applied from
 * .claude/skills/ahoy-ux-copy: sentence case for headings, toggles and body;
 * en-dashes; Oxford comma for 3+; no internal ontology; the closed state
 * vocabulary (Active / Paused / On / Off / Done). Eyebrow labels are written
 * in natural case and uppercased by CSS. Chevrons are components, never
 * characters in these strings.
 */

import type {
  AutonomyStop,
  CapabilityKey,
  ContactsVerb,
  PipelineVerb,
} from './autonomyPresets';
import { DEFAULT_AUTONOMY_STOP } from './autonomyPresets';

/** "Deals" / "Deals and Tasks" / "Deals, Tasks, and Tickets". */
export function joinWithAnd(names: readonly string[]): string {
  if (names.length <= 1) {
    return names[0] ?? '';
  }
  if (names.length === 2) {
    return `${names[0]} and ${names[1]}`;
  }
  return `${names.slice(0, -1).join(', ')}, and ${names[names.length - 1]}`;
}

/** A label inside a sentence: first letter down, the rest as written (a
 *  customer's stage name keeps its own casing). */
export function lowerFirst(label: string): string {
  return label.charAt(0).toLowerCase() + label.slice(1);
}

// TODO(copy): final wording is Laura's
export const SECTION_TITLE = 'Agent autonomy';
// TODO(copy): final wording is Laura's
export const SECTION_DESCRIPTION =
  'Choose how much your agent does on its own. Everything else waits for you.';

// Header row. The toggle's accessible name stays "Active" in both states;
// the visible title shows the closed-vocabulary state word.
// TODO(copy): final wording is Laura's
export const ACTIVE_LABEL = 'Active';
export const PAUSED_LABEL = 'Paused';
export const ACTIVE_DESCRIPTION =
  'Updates your records on its own, within the limits below.';
export const PAUSED_DESCRIPTION_PREFIX = 'Only suggests. Will resume at';

// Level zone. TODO(copy): final wording is Laura's
export const LEVEL_EYEBROW = 'How much it does on its own';
export const LEVEL_STEPPER_ARIA_LABEL = 'How much your agent does on its own';

/** Stop names. TODO(copy): final wording is Laura's. */
export const STOP_LABELS: Record<AutonomyStop, string> = {
  0: 'Basics',
  1: 'Routine updates',
  2: 'Most updates',
  3: 'Everything it can',
};

/**
 * The capability ladder: everything the agent can do, grouped by the stop
 * that unlocks it. TODO(copy): final wording is Laura's.
 */
export const CAPABILITY_LABELS: Record<CapabilityKey, string> = {
  notes: 'Keeps notes and meeting summaries',
  link: 'Links your activity to the right records',
  fill: 'Fills in details that were never set',
  contacts: 'Adds contacts and companies',
  create: 'Creates records in your pipelines and moves them forward',
  terminal: 'Marks records won, lost, or done',
  backward: 'Moves records back a stage',
  assign: 'Assigns records to teammates',
  merge: 'Merges duplicates',
  edit: 'Changes details someone entered',
};

/** A hint on a ladder stop, only where it carries information. The default
 *  stop is marked so there is a visibly safe place to be.
 *  TODO(copy): final wording is Laura's. */
export const GROUP_HINTS: Partial<Record<AutonomyStop, string>> = {
  0: 'Always on',
  [DEFAULT_AUTONOMY_STOP]: 'Recommended',
  3: 'Harder to undo',
};

/**
 * One line per stop for the ladder's collapsed rows — what that stop adds,
 * as a person would say it. Hand-written rather than assembled from the
 * capability labels so each reads as a sentence. TODO(copy): final wording
 * is Laura's.
 */
export const STOP_SUMMARIES: Record<AutonomyStop, string> = {
  0: 'Notes, meeting summaries, linking activity, and filling in blanks',
  1: 'Adds contacts and companies, creates and advances pipeline records',
  2: 'Marks won, lost, or done, moves back, assigns to teammates',
  3: 'Merges duplicates and changes details someone entered',
};

/** "Deals only" / "Deals and Tasks only" / "2 of 3" for a partial row.
 *  TODO(copy): final wording is Laura's. */
export function describePartialCoverage(
  onLabels: string[],
  total: number
): string {
  if (onLabels.length === 1) {
    return `${onLabels[0]} only`;
  }
  if (onLabels.length === 2) {
    return `${onLabels[0]} and ${onLabels[1]} only`;
  }
  return `${onLabels.length} of ${total}`;
}

// The locked row at the bottom of the ladder. TODO(copy): final wording is
// Laura's.
export const ALWAYS_ASKS_ROW_LABEL = 'Always waits for you';
export const ALWAYS_ASKS_ROW_SUMMARY =
  'Sending email, deleting your records, and more';
export const ALWAYS_ASKS_INTRO =
  'Your agent never sends email on its own. These always wait for you, whatever you turn on:';
export const ALWAYS_ASKS_ITEMS = [
  'Deleting anything you created or edited yourself',
  'Moving work to a different pipeline',
  'Sharing records or changing who can see them',
  "Adding or changing a teammate's own details",
  'Adding an anonymous website visitor as a person',
] as const;
export const ALWAYS_ASKS_FOOTER = 'No setting changes these.';

// Customized state. TODO(copy): final wording is Laura's
export const CUSTOMIZED_BADGE = 'Customized';
export const RESET_LABEL_PREFIX = 'Reset to';

// Where zone. TODO(copy): final wording is Laura's
export const WHERE_EYEBROW = 'Where it works';
export const CUSTOMIZE_LABEL = 'Customize';
export const OFF_SUFFIX = 'off';
export const CONTACTS_SCOPE_LABEL = 'Contacts & companies';
export const NEW_PIPELINES_NOTE = 'New pipelines start off.';

// The tune panel. Its one job is to make "nothing unusual is set" read as
// calm: an intro that says the panel is optional, a status sentence on every
// scope, and a divider that separates the verbs the level already includes
// from the ones that would be an exception. TODO(copy): final wording is
// Laura's.
export const TUNE_INTRO =
  'Turn a pipeline off, or make an exception for one. Everything else follows the level above.';
/** "Follows Most updates" */
export const SCOPE_FOLLOWS_PREFIX = 'Follows';
/** "More than Most updates – merge duplicates" */
export const SCOPE_MORE_THAN_PREFIX = 'More than';
/** "Less than Most updates – assign to teammates off" */
export const SCOPE_LESS_THAN_PREFIX = 'Less than';
/** "Differs from Most updates – merge duplicates on, assign to teammates off" */
export const SCOPE_DIFFERS_PREFIX = 'Differs from';
export const SCOPE_OFF = 'Off';
/** "Beyond Most updates" — the divider before a scope's extra verbs. */
export const BEYOND_LEVEL_PREFIX = 'Beyond';

// Verb rows. The mark-completed row keeps the customer's own terminal stage
// names when a pipeline has them (customer text, verbatim) and falls back to
// the closed state vocabulary's "done". TODO(copy): final wording is Laura's.
export const MOVE_BACK_LABEL = 'Move back a stage';
export const MERGE_LABEL = 'Merge duplicates';
export const ASSIGN_LABEL = 'Assign to teammates';
export const EDIT_LABEL = 'Change details someone entered';
export const MARK_COMPLETED_FALLBACK_LABEL = 'Mark as done';

/** Generic verb names for toasts and summaries, where the per-pipeline
 *  terminal stage names are not to hand. */
export const PIPELINE_VERB_LABELS: Record<PipelineVerb, string> = {
  markCompleted: MARK_COMPLETED_FALLBACK_LABEL,
  moveBackward: MOVE_BACK_LABEL,
  mergeDuplicates: MERGE_LABEL,
  assignToTeammates: ASSIGN_LABEL,
  editEnteredDetails: EDIT_LABEL,
};
export const CONTACTS_VERB_LABELS: Record<ContactsVerb, string> = {
  mergeContacts: MERGE_LABEL,
  assignContacts: ASSIGN_LABEL,
  editContacts: EDIT_LABEL,
};

// Coverage phrasing for toasts. TODO(copy): final wording is Laura's.
export const COVERAGE_NONE = 'Nothing is turned on yet.';
export const COVERAGE_CONTACTS_ONLY = 'For contacts & companies only.';
export const COVERAGE_ALL_PREFIX = 'For contacts & companies and';
export const COVERAGE_PIPELINES_ONLY_PREFIX = 'For';
export const COVERAGE_CONTACTS_OFF_SUFFIX = 'contacts & companies off';

// Suggestions group. TODO(copy): final wording is Laura's.
export const SUGGESTS_HEADING = 'Suggests';
export const NON_PIPELINE_LABEL = 'Suggest work outside your pipelines';
export const NON_PIPELINE_DESCRIPTION =
  'Like investor updates or inbound job applications. Always a suggestion, never automatic.';
export const NON_PIPELINE_PRIVACY = 'Your actions stay private to you.';
export const NON_PIPELINE_SAVE_ERROR_TITLE = "Couldn't save your preference";
export const NON_PIPELINE_BLOCKED_DESCRIPTION =
  "This setting can't be changed until the issue is resolved.";

// Status / errors. TODO(copy): final wording is Laura's.
export const LOADING_LABEL = 'Loading…';
export const ABSENT_TITLE = "Couldn't load your agent";
export const ABSENT_DESCRIPTION =
  "We couldn't find your agent's settings. Check your connection and try again.";
export const RETRY_LABEL = 'Retry';
export const ERROR_TITLE = "Couldn't load your agent settings";
export const ERROR_DESCRIPTION =
  "We couldn't load your agent settings. Try refreshing the page.";

// Toasts. Every write names exactly what changed and where, and offers Undo,
// so a misclick costs one more click. TODO(copy): final wording is Laura's.
export const TOAST_UNDO = 'Undo';
export const TOAST_SET_TO_PREFIX = 'Set to';
export const TOAST_RESET_PREFIX = 'Reset to';
/** "Merge duplicates – on for Deals and Tasks" */
export const TOAST_ON = 'on';
export const TOAST_OFF = 'off';
export const TOAST_FOR = 'for';
/** "Deals – on at Most updates" */
export const TOAST_AT = 'at';
export const TOAST_ACTIVE_RESUMING_PREFIX = 'Active – resuming at';
export const TOAST_ACTIVE_SET_PREFIX = 'Active – set to';
export const TOAST_PAUSED = 'Paused – your agent will only suggest';
